import { useState, useEffect, useRef } from 'react';
import { X, Star, Camera } from 'lucide-react';
import { toast } from 'react-hot-toast';
import reviewService from '../../services/reviewService';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryId: string;
}

export default function ReviewModal({
  isOpen,
  onClose,
  deliveryId
}: ReviewModalProps) {
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [hasReviewed, setHasReviewed] = useState<boolean>(false);
  
  // Reference to track valid images
  const lastValidImagesRef = useRef<File[]>([]);
  const uploadedImageUrlsRef = useRef<string[]>([]);

  // Check if delivery has already been reviewed
  useEffect(() => {
    if (isOpen && deliveryId) {
      const checkReviewStatus = async () => {
        try {
          const reviewed = await reviewService.hasReviewed(deliveryId);
          setHasReviewed(reviewed);
          
          // If already reviewed, fetch the existing review
          if (reviewed) {
            const review = await reviewService.getReviewByDeliveryId(deliveryId);
            if (review) {
              setRating(review.rating);
              setComment(review.comment || '');
              
              // Set image URLs if available
              if (review.images && review.images.length > 0) {
                setImageUrls(review.images);
                uploadedImageUrlsRef.current = review.images;
              }
            }
          }
        } catch (error) {
          console.error('Error checking review status:', error);
        }
      };
      
      checkReviewStatus();
    }
  }, [isOpen, deliveryId]);

  // Cleanup effect for image URLs
  useEffect(() => {
    return () => {
      // Cleanup blob URLs when component unmounts
      imageUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  if (!isOpen) return null;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      try {
        // Create a copy of current images and URLs
        const currentImages = [...images];
        const currentUrls = [...imageUrls];
        
        // Get new files and limit total to 3
        const newFiles = Array.from(e.target.files);
        const totalFiles = [...currentImages, ...newFiles].slice(0, 3);
        
        // Create new URL objects for preview
        const newImageUrls = totalFiles.map((file, index) => {
          // If we already have a URL for this index, use it
          if (index < currentUrls.length) {
            return currentUrls[index];
          }
          // Otherwise create a new blob URL
          return URL.createObjectURL(file);
        });
        
        // Save valid images to state and ref
        setImages(totalFiles);
        lastValidImagesRef.current = totalFiles;
        
        // Clean up any old blob URLs that won't be used
        currentUrls.forEach((url, index) => {
          if (url.startsWith('blob:') && index >= newImageUrls.length) {
            URL.revokeObjectURL(url);
          }
        });
        
        setImageUrls(newImageUrls);
        console.log('Updated images array with new files', totalFiles);
      } catch (error) {
        console.error('Error handling image upload:', error);
        toast.error('Failed to process image. Please try again.');
      }
    }
  };

  const removeImage = (index: number) => {
    try {
      // Create a copy of current arrays
      const currentImages = [...images];
      const currentUrls = [...imageUrls];
      
      // If URL is a blob URL, revoke it
      if (currentUrls[index]?.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrls[index]);
      }
      
      // Remove the image and URL from arrays
      const newImages = currentImages.filter((_, i) => i !== index);
      const newUrls = currentUrls.filter((_, i) => i !== index);
      
      // Update state and refs
      setImages(newImages);
      setImageUrls(newUrls);
      lastValidImagesRef.current = newImages;
      
      console.log('Removed image at index', index);
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);

      // Use the improved review service
      await reviewService.submitReview({
        deliveryId,
        rating,
        comment: comment.trim() || undefined,
        images: images.length > 0 ? images : undefined,
        existingImages: uploadedImageUrlsRef.current.length > 0 ? uploadedImageUrlsRef.current : undefined
      });

      toast.success(hasReviewed ? 'Review updated successfully' : 'Review submitted successfully');
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please check API endpoint configuration.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:rounded-lg sm:shadow-xl sm:max-w-md overflow-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b bg-white">
          <h2 className="text-lg font-semibold">
            {hasReviewed ? 'Edit Your Review' : 'Review Your Delivery'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Rating */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              How would you rate your delivery?
            </label>
            <div className="flex gap-1 sm:gap-2 justify-center sm:justify-start">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  <Star
                    size={24}
                    className={`sm:w-8 sm:h-8 transition-all duration-150 ${
                      star <= rating ? 'fill-amber-500 stroke-amber-500' : 'fill-none stroke-gray-400'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          
          {/* Comment */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Share your feedback (optional)
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you like or dislike about your delivery?"
            />
          </div>
          
          {/* Image Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Add photos (optional)
            </label>
            
            {/* Image Previews - Grid layout similar to AddressForm */}
            {imageUrls.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {imageUrls.map((url, index) => (
                  <div key={index} className="relative w-full aspect-square">
                    <img
                      src={url}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            {imageUrls.length < 3 && (
              <div className="w-full">
                <label 
                  htmlFor="image-upload" 
                  className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex flex-col items-center">
                    <Camera size={24} className="text-gray-400 mb-1" />
                    <span className="text-sm text-gray-500">
                      {imageUrls.length === 0 
                        ? 'Upload photos' 
                        : `Add more (${3 - imageUrls.length} remaining)`}
                    </span>
                  </div>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={imageUrls.length >= 3}
                  />
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Max 3 photos, each up to 5MB
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="sticky bottom-0 z-10 p-4 border-t bg-white">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              submitting || rating === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
          >
            {submitting ? 'Submitting...' : hasReviewed ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
} 